import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { fetchAuthSession } from "aws-amplify/auth";
import Onboard from "../../components/screens/Onboard/OnboardScreen";
import Dialog from "@mui/material/Dialog";
import learnImage from "../../img/0-nuala-001.svg";
import teachImage from "../../img/1-teacher-001.svg";
import { updateUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import config from "../../config";
import { searchStringToObj, objToSearchString } from "../../utils";
import { useApp, useSubscription } from "../../hooks";

import logger from "../../logger";

function Welcome(props) {
  const { authData, navigate, location } = props;

  const { t } = useTranslation();

  const { openSnackbar } = useApp();
  const { subscription } = useSubscription();

  const searchParams = searchStringToObj(location.search);
  const redirectSearchParams =
    searchParams && searchParams.redirect
      ? searchStringToObj(new URL(searchParams.redirect).search)
      : null;

  /*
    Onboarding only forwards `plan`, `learnlang` and `redirect` to /welcome, so the
    remaining checkout params have to be read back off the redirect url. Without
    them the paid checkout branch below can never be satisfied and the user is
    dropped into a free trial with their plan and promotion silently discarded.
  */
  const {
    plan = redirectSearchParams?.plan,
    learnlang = redirectSearchParams?.learnlang,
    students = redirectSearchParams?.students,
    interval = redirectSearchParams?.interval,
    currency = redirectSearchParams?.currency,
    promotion_code = redirectSearchParams?.promotion_code,
    coupon = redirectSearchParams?.coupon,
    redirect,
  } = searchParams;

  /*
    `redirect` is where to land once checkout finishes. When it points back at the
    payment page it is self referential — every param it carries has already been
    rebuilt into the url below — so forwarding it would send the user back through
    checkout again on return.
  */
  const isRedirectToPayment = () => {
    try {
      return (
        new URL(redirect, window.location.origin).pathname.replace(/\/$/, "") ===
        "/payment"
      );
    } catch {
      return false;
    }
  };

  const handleOnboardingComplete = async () => {
    // Refresh the local jwt tokens before navigating
    const session = await fetchAuthSession({ forceRefresh: true });
    const attributes = session?.tokens.idToken.payload;
    await invalidateCache(DataType.USER, {
      userId: authData.username,
    });
    //if teach is selected then a pricing plan must be selected
    if (attributes && attributes["custom:role"] === "teach") {
      if (subscription.isPaidUser) {
        if (redirect) {
          window.location = redirect;
        } else {
          navigate("/explore");
        }
      } else if (
        redirect &&
        redirect.includes("join") &&
        redirect.includes("groups")
      ) {
        //if they joined by group invite link
        window.location = redirect;
      } else if (plan && students && interval && currency) {
        window.location = `/payment/?${objToSearchString({
          plan,
          students,
          interval,
          currency,
          redirect: redirect && !isRedirectToPayment() ? redirect : undefined,
          // eslint-disable-next-line camelcase
          promotion_code,
          coupon,
        })}`;
      } else {
        window.location.href = `${config.APP_URL}/start-trial/${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`;
      }
    } else if (redirect) {
      window.location = redirect;
    } else {
      navigate("/explore");
    }
  };

  const handleSelectRole = async (role) => {
    try {
      // If learnLang has already been set then finish onboarding
      if (
        authData &&
        authData.attributes &&
        authData.attributes["custom:learnLang"]
      ) {
        await updateUser(authData.username, {
          "custom:role": role,
        });
        // await invalidateCache(DataType.USER, {
        //   userId: authData.username,
        // });
        handleOnboardingComplete();
      } else if (role === "teach" && learnlang) {
        await updateUser(authData.username, {
          "custom:learnLang": learnlang,
          "custom:role": role,
        });
        handleOnboardingComplete();
      } else {
        // no await on updateUser to prevent delay moving to the next step
        updateUser(authData.username, {
          "custom:role": role,
        });
      }
    } catch (error) {
      logger.error(error);
      openSnackbar(error.message, "error");
    }
  };

  const handleSelectLanguage = async (language) => {
    try {
      if (plan === "teacher_pro" && !authData.attributes["custom:role"]) {
        await updateUser(authData.username, {
          "custom:learnLang": language,
          "custom:role": "teach",
        });
      } else {
        await updateUser(authData.username, {
          "custom:learnLang": language,
        });
      }
      // await invalidateCache(DataType.USER, {
      //   userId: authData.username,
      // });
      handleOnboardingComplete();
    } catch (error) {
      logger.error(error);
      openSnackbar(error.message, "error");
    }
  };

  return (
    <Dialog
      fullScreen
      open={true}
      transitionDuration={{
        enter: 0,
      }}
    >
      <Onboard
        t={t}
        // benefits={benefits}
        handleSelectRole={handleSelectRole}
        handleSelectLanguage={handleSelectLanguage}
        roleOptions={[
          {
            img: learnImage,
            role: "learn",
          },
          {
            img: teachImage,
            role: "teach",
          },
        ]}
        plan={plan}
        preferred_username={authData.attributes.preferred_username}
        learnlang={learnlang}
      />
    </Dialog>
  );
}

Welcome.propTypes = {
  authData: PropTypes.object,
};

export default Welcome;
